import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Ship, MapPin, Calendar, Gauge, ArrowRight, CheckCircle, ArrowLeft } from 'lucide-react';
import PublicHeader from './PublicHeader';
import PublicFooter from './PublicFooter';
import VehicleCard from './VehicleCard';
import ScrollToTop from './ScrollToTop';
import WhatsAppButton from './WhatsAppButton';
import SEOHead from './SEOHead';
import { API } from '../../config';
import { getShipCountry } from '../../data/shipCountries';

const ShippedCountryPage = () => {
  const { country } = useParams();
  const navigate = useNavigate();
  const shipCountry = getShipCountry(country);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!shipCountry) return;
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/public/shipped-vehicles/${shipCountry.slug}`)
      .then((response) => {
        if (!cancelled) setVehicles(response.data || []);
      })
      .catch((error) => {
        console.error(`Error fetching ${shipCountry.slug} shipped vehicles:`, error);
        if (!cancelled) setVehicles([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shipCountry]);

  if (!shipCountry) {
    return <Navigate to="/shipped" replace />;
  }

  const pageTitle = `Vehicles Shipped to ${shipCountry.name} - Global Trade Car`;
  const pageDescription = `Japanese used vehicles currently on their way to ${shipCountry.name}. Browse our real shipments to ${shipCountry.name} and let Global Trade Car handle your export journey.`;

  const listStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: pageTitle,
    description: pageDescription,
    numberOfItems: vehicles.length,
    itemListElement: vehicles.slice(0, 20).map((v, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `https://globaltradecar.com/vehicles/${v.id}`,
      name: `${v.year ? v.year + ' ' : ''}${v.brand || ''} ${v.model || ''}`.trim(),
    })),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={`${shipCountry.name} vehicles, shipped to ${shipCountry.name}, Japan car export ${shipCountry.name}, Global Trade Car`}
        structuredData={listStructuredData}
      />
      <PublicHeader />

      {/* Hero Section */}
      <div className="relative overflow-hidden text-white">
        <img
          src={shipCountry.heroImage}
          alt={`${shipCountry.name} hero`}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <button
            onClick={() => navigate('/shipped')}
            className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            All Shipped Vehicles
          </button>
          <div className="flex items-center gap-4 mb-4">
            <img
              src={shipCountry.flag}
              alt={`${shipCountry.name} flag`}
              className="h-10 w-auto rounded shadow-lg ring-2 ring-white/30"
            />
            <Badge className="bg-white/20 text-white text-sm px-3 py-1 backdrop-blur-sm">
              On the Way to {shipCountry.name}
            </Badge>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Vehicles Heading to {shipCountry.name}
          </h1>
          <p className="text-lg text-white/85 max-w-2xl mb-8">
            {shipCountry.description}
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 text-sm text-white/85">
              <CheckCircle className="h-4 w-4 text-white/70" />
              <span>Full shipping documentation</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-white/85">
              <MapPin className="h-4 w-4" />
              <span>Tracked until arrival</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      {!loading && vehicles.length > 0 && (
        <div className="bg-white border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <p className="text-slate-600">
                <span className="font-bold text-slate-900">{vehicles.length}</span> vehicle{vehicles.length !== 1 ? 's' : ''} on the way to {shipCountry.name}
              </p>
              <Button
                variant="outline"
                onClick={() => navigate('/vehicles')}
                className="text-cyan-600 border-cyan-200 hover:bg-cyan-50"
              >
                Browse Available Vehicles
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Vehicles Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="animate-pulse">
                <div className="h-48 bg-gray-200"></div>
                <CardContent className="p-6">
                  <div className="h-6 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : vehicles.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {vehicles.map((vehicle) => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} showShipCountry showEta />
            ))}
          </div>
        ) : (
          <Card className="mt-8">
            <CardContent className="p-12 text-center">
              <Ship className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                No Vehicles Shipped to {shipCountry.name} Yet
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                We haven't shipped any vehicles to {shipCountry.name} yet. Check
                back soon or browse our available inventory to be the first!
              </p>
              <Button
                onClick={() => navigate('/vehicles')}
                className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700"
              >
                Browse Available Vehicles
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-r from-cyan-600 to-blue-600 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Ship Your Next Vehicle to {shipCountry.name}?</h2>
          <p className="text-lg text-cyan-100 mb-6 max-w-2xl mx-auto">
            Join our growing list of satisfied customers in {shipCountry.name}. Browse
            our inventory and start your export journey today.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Button
              size="lg"
              onClick={() => navigate('/vehicles')}
              className="bg-white text-cyan-700 hover:bg-cyan-50 font-semibold"
            >
              Browse Inventory
              <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/contact')}
              className="border-white text-white hover:bg-white/10 font-semibold"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </div>

      <PublicFooter />
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default ShippedCountryPage;
